import React from 'react';
import { useForm } from 'react-hook-form';
import { Cancel, Email, Phone } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Drawer,
  Unstable_Grid2 as Grid,
  IconButton,
  InputAdornment,
  Stack
} from '@mui/material';
import mapValues from 'lodash/mapValues';

import { ControlledDatePicker } from '@/components/ControlledDatePicker';
import { ControlledProfilePicture } from '@/components/ControlledProfilePicture';
import { ControlledRadioGroup } from '@/components/ControlledRadioGroup';
import { ControlledSelectPrimitive } from '@/components/ControlledSelectPrimitive';
import { ControlledTextField } from '@/components/ControlledTextField';
import { EMAIL_REGEX } from '@/constants';
import { useCareLevelsQuery } from '@/hooks/useCareLevelsQuery';
import { useFindAllRacesQuery } from '@/hooks/useRacesQuery';
import {
  Religion,
  ResidentPayload,
  useResidentFacesheetMutation
} from '@/hooks/useResidentFacesheetsQuery';
import {
  ResidentGroup,
  useResidentGroupsQuery
} from '@/hooks/useResidentGroupsQuery';
import { GenericEnumOption } from '@/stores/residentFacesheetsAtom';

import AddCohabitantSection from './components/AddCohabitantSection';
import ResidentLanguagesInputs from './components/ResidentLanguagesInputs';
import {
  LanguageOptions,
  LanguagePayload,
  Language as LanguageType,
  useLanguagesQuery
} from './hooks/useLanguagesQuery';

export const indexToLabelMap: Record<number, string> = {
  0: 'First',
  1: 'Second',
  2: 'Third'
};

export const maritalStatusOptions: GenericEnumOption[] = [
  {
    label: 'Single',
    value: 'single'
  },
  {
    label: 'Married',
    value: 'married'
  },
  {
    label: 'Widowed',
    value: 'widowed'
  },
  {
    label: 'Divorced',
    value: 'divorced'
  },
  {
    label: 'Significant Other',
    value: 'significant_other'
  },
  {
    label: 'Companion',
    value: 'companion'
  }
];

export const genderOptions: GenericEnumOption[] = [
  {
    label: 'Male',
    value: 'male'
  },
  {
    label: 'Female',
    value: 'female'
  },
  {
    label: 'Other',
    value: 'other'
  }
];

const religionOptions: Religion[] = [
  Religion.PROTESTANT,
  Religion.CATHOLIC,
  Religion.MORMON,
  Religion.ORTHODOX,
  Religion.UNAFFILIATED,
  Religion.ATHEIST,
  Religion.AGNOSTIC,
  Religion.JEWISH,
  Religion.MUSLIM,
  Religion.HINDU,
  Religion.BUDDHIST,
  Religion.OTHER_CHRISTIAN,
  Religion.OTHER
];

export interface EditResidentFacesheetForm {
  id: string;
  s3_photo_key: string | null;
  first_name: string;
  last_name: string;
  nickname: string | null;
  dob: string | null;
  phone: string | null;
  email: string | null;
  facility_id: string | null;
  resident_group_id: string | null;
  room_id: string | null;
  service_start_date: string | null;
  care_level_id: string | null;
  gender: string | null;
  marital_status: string | null;
  race_id: string | null;
  ssn: string | null;
  authorized_rep: string | null;
  religion: string | null;
  previous_occupation: string | null;
  previous_residence: string | null;
  place_of_birth: string | null;
  biography: string | null;
  is_veteran: boolean;
  is_veteran_spouse: boolean;
  cohabitant_id: string | null;
  cohabitant?: (Partial<ResidentPayload> & { relationship?: string }) | null;
}

interface EditResidentFacesheetDrawerProps {
  resident: ResidentPayload;
  languages?: LanguageType[];
  isCRM: boolean;
  onResolve: VoidFunction;
  isOpen: boolean;
}

export const EditResidentFacesheetDrawer: React.FC<
  EditResidentFacesheetDrawerProps
> = ({ resident, onResolve, isCRM, isOpen, languages }) => {
  // WARNING: Don't type this to EditResidentFacesheetForm or tsc resources
  // will spike
  const formMethods = useForm<any>({
    defaultValues: resident,
    mode: 'all'
  });

  const residentLanguageMutation = useLanguagesQuery().mutations(resident.id);

  const languagesFormMethods = useForm({
    defaultValues: {
      primary_language: languages?.find(({ is_primary }) => is_primary)
        ?.language,
      secondary_languages:
        languages
          ?.filter(({ is_primary }) => !is_primary)
          .map(({ language }) => language) ?? []
    }
  });

  const facilityId = formMethods.watch('facility_id');

  const { data: races = [] } = useFindAllRacesQuery();
  const { data: careLevels = [] } = useCareLevelsQuery().findAll();
  const { data: residentGroups = [] } = useResidentGroupsQuery().findAll(
    false,
    facilityId
  );

  const mutation = useResidentFacesheetMutation();

  const handleResidentInfoSave = formMethods.handleSubmit(
    (values) => {
      const valuesWithEmptyStringsConvertedToNull = mapValues(
        values,
        (value) => (value === '' ? null : value)
      ) as ResidentPayload;
      mutation.mutate(valuesWithEmptyStringsConvertedToNull, {
        onSuccess: () => onResolve()
      });
    },
    (errors) => {
      const name = Object.keys(errors)[0];
      setTimeout(() => {
        const element = document.querySelector(
          `input.MuiSelect-nativeInput[name="${name}"]`
        ) as HTMLInputElement;
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    }
  );

  const defineLanguageId = (language: LanguageOptions) => {
    const lang = languages?.find(({ language: lang }) => lang === language);
    return lang?.id;
  };

  const handleLanguagesSave = languagesFormMethods.handleSubmit(
    async (languages) => {
      // For languanges that already have on a resident DB, pass the lang id (UPDATE)
      // For languages that are not on this resident languages array, don't pass the lang id (CREATE)
      // For DELETE, don't pass anything at all.
      const { primary_language, secondary_languages } = languages;
      const body: LanguagePayload =
        primary_language == null || (primary_language as string) === ''
          ? []
          : [
              {
                language: primary_language as LanguageOptions,
                is_primary: true,
                id: defineLanguageId(primary_language as LanguageOptions)
              }
            ];

      if (secondary_languages.length > 0) {
        secondary_languages.forEach((language) => {
          body.push({
            language: language as LanguageOptions,
            is_primary: false,
            id: defineLanguageId(language as LanguageOptions)
          });
        });
      }
      await residentLanguageMutation.update.mutateAsync(body);
    }
  );

  const handleBulkSave = () => {
    handleResidentInfoSave();
    handleLanguagesSave();
  };

  return (
    <Drawer
      open={isOpen}
      onClose={() => onResolve()}
      PaperProps={{
        className: 'drawer-paper'
      }}
    >
      <Stack component="header">
        Edit Resident Information
        <IconButton onClick={() => onResolve()}>
          <Cancel />
        </IconButton>
      </Stack>

      <Stack className="drawer-paper-content">
        <Grid container spacing={2}>
          <Grid xs={12}>
            <ControlledProfilePicture
              formMethods={formMethods}
              name="s3_photo_key"
            />
          </Grid>

          <Grid xs={12} md={6}>
            <ControlledTextField
              control={formMethods.control}
              name="first_name"
              label="First Name"
              rules={{ required: true }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <ControlledTextField
              control={formMethods.control}
              name="last_name"
              label="Last Name"
              rules={{ required: true }}
            />
          </Grid>
          <Grid xs={12}>
            <ControlledTextField
              control={formMethods.control}
              name="nickname"
              label="Preferred Name"
            />
          </Grid>
          <Grid xs={12} md={6}>
            <ControlledSelectPrimitive
              control={formMethods.control}
              options={genderOptions}
              name="gender"
              label="Gender"
              optionLabelKey="label"
              optionIdKey="value"
            />
          </Grid>
          <Grid xs={12} md={6}>
            <ControlledDatePicker
              control={formMethods.control}
              name="dob"
              label="Birthdate"
              rules={{ required: !isCRM }}
              disableFuture
            />
          </Grid>
          <Grid xs={12}>
            <ControlledSelectPrimitive
              control={formMethods.control}
              options={maritalStatusOptions}
              name="marital_status"
              label="Marital Status"
              optionLabelKey="label"
              optionIdKey="value"
            />
          </Grid>
          <Grid xs={12}>
            <ControlledTextField
              control={formMethods.control}
              label="Authorized Representative"
              type="text"
              name="authorized_rep"
            />
          </Grid>
          <Grid xs={12} md={6}>
            <ControlledTextField
              control={formMethods.control}
              label="Phone Number"
              name="phone"
              format="phoneNumber"
              placeholder="(###) ###-####"
              rules={{
                minLength: {
                  value: 10,
                  message: 'Invalid phone number'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Phone />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <ControlledTextField
              sx={{ color: 'rgba(0, 155, 208, 1)' }}
              control={formMethods.control}
              label="Email"
              name="email"
              rules={{
                pattern: EMAIL_REGEX
              }}
              placeholder="john.smith@exacare.com"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Email />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid xs={12}>
            <ControlledTextField
              className="no-upscope"
              control={formMethods.control}
              label="Social Security Number"
              name="ssn"
              placeholder="#########"
              format="ssn"
              rules={{ minLength: 9 }}
            />
          </Grid>
          {!isCRM && (
            <Grid xs={12}>
              <ControlledSelectPrimitive
                control={formMethods.control}
                options={residentGroups as ResidentGroup[]}
                name="resident_group_id"
                label="Resident Group"
                optionLabelKey="name"
                optionIdKey="id"
                onChange={(value) => {
                  if (value === '') {
                    formMethods.setValue('resident_group_id', null);
                  }
                }}
              />
            </Grid>
          )}
          <Grid xs={12}>
            <ControlledDatePicker
              control={formMethods.control}
              name="service_start_date"
              label="Physical Move-In Date"
              disableFuture
            />
          </Grid>
          <Grid xs={12}>
            <ControlledSelectPrimitive
              control={formMethods.control}
              options={careLevels}
              name="care_level_id"
              label="Care Level"
              optionIdKey="id"
              optionLabelKey="title"
            />
          </Grid>
          {/* TODO Contact Method Preference */}
          {/* TODO: Bill Delivery Preference */}
          <ResidentLanguagesInputs
            formMethods={languagesFormMethods}
            initialSecondaryLanguages={
              languagesFormMethods.getValues(
                'secondary_languages'
              ) as unknown as string[]
            }
          />
          <Grid xs={12} md={6}>
            <ControlledSelectPrimitive
              control={formMethods.control}
              options={races}
              name="race_id"
              label="Ethnicity"
              optionLabelKey="name"
              optionIdKey="id"
            />
          </Grid>
          <Grid xs={12} md={6}>
            <ControlledSelectPrimitive
              control={formMethods.control}
              options={religionOptions}
              rules={{ required: false }}
              name="religion"
              label="Religion"
            />
          </Grid>
          <Grid xs={6}>
            <ControlledRadioGroup
              name="is_veteran"
              control={formMethods.control}
              label="Veteran"
              fields={[
                {
                  label: 'Yes',
                  value: true
                },
                {
                  label: 'No',
                  value: false
                }
              ]}
            />
          </Grid>
          <Grid xs={6}>
            <ControlledRadioGroup
              name="is_veteran_spouse"
              control={formMethods.control}
              label="Spouse of Veteran"
              fields={[
                {
                  label: 'Yes',
                  value: true
                },
                {
                  label: 'No',
                  value: false
                }
              ]}
            />
          </Grid>
          <Grid xs={12}>
            <ControlledTextField
              control={formMethods.control}
              label="Previous Occupation"
              placeholder="Mechanic"
              type="text"
              name="previous_occupation"
            />
          </Grid>
          <Grid xs={12}>
            <ControlledTextField
              control={formMethods.control}
              label="Previous Residence"
              placeholder="77 Durham St. Garland, TX 75041"
              type="text"
              name="previous_residence"
            />
          </Grid>
          <Grid xs={12}>
            <ControlledTextField
              control={formMethods.control}
              label="Place of Birth"
              placeholder="Dallas, Texas"
              type="text"
              name="place_of_birth"
            />
          </Grid>
          <Grid xs={12}>
            <ControlledTextField
              control={formMethods.control}
              label="Biography"
              multiline
              rows={4}
              placeholder="This is a brief snippet on what the resident likes and doesn't like, where they were raised, etc."
              type="text"
              name="biography"
            />
          </Grid>

          {!isCRM && (
            <Grid xs={12}>
              <AddCohabitantSection
                residentId={resident.id}
                formMethods={formMethods}
                cohabitantPayload={resident.cohabitant}
              />
            </Grid>
          )}
        </Grid>
      </Stack>

      <Stack component="footer">
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => onResolve()}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={mutation.isLoading}
          loadingPosition="start"
          variant="contained"
          onClick={handleBulkSave}
        >
          Save Changes
        </LoadingButton>
      </Stack>
    </Drawer>
  );
};
